import type { PriceBand } from "@/types/work";

/**
 * 価格帯の定義（判定とラベルの唯一の情報源）。
 *
 * 以前は「判定は facets.ts」「ラベルは searchParams.ts / FilterPanel.tsx / labels.ts」に
 * 分散していたため、3,001円以上が「3,000円以上」と表示される不整合が起きていた。
 * ここに範囲とラベルをまとめ、UI・URL・集計はすべてこの定義を参照する。
 *
 * 帯は重複も欠落もさせない：
 *   0円          → free
 *   1〜499円     → under500
 *   500〜999円   → 500to1000
 *   1,000〜3,000円 → 1000to3000
 *   3,001円以上  → over3000
 *
 * 帯のIDは保存済みの検索条件・URL（price=1000to3000 など）と互換のため変更しない。
 */
export interface PriceBandDefinition {
  id: PriceBand;
  /** 絞り込みUIのラベル（範囲がそのまま分かる表記） */
  label: string;
  /** チップなど狭い場所向けの短いラベル */
  shortLabel: string;
  /** 下限（含む） */
  min: number;
  /** 上限（含む）。null は上限なし */
  max: number | null;
}

export const PRICE_BANDS: PriceBandDefinition[] = [
  { id: "free", label: "無料（0円）", shortLabel: "無料", min: 0, max: 0 },
  { id: "under500", label: "1〜499円", shortLabel: "500円未満", min: 1, max: 499 },
  { id: "500to1000", label: "500〜999円", shortLabel: "500〜999円", min: 500, max: 999 },
  { id: "1000to3000", label: "1,000〜3,000円", shortLabel: "1,000〜3,000円", min: 1000, max: 3000 },
  { id: "over3000", label: "3,001円以上", shortLabel: "3,001円以上", min: 3001, max: null },
];

const bandById = new Map<PriceBand, PriceBandDefinition>(PRICE_BANDS.map((band) => [band.id, band]));

export function getPriceBandDefinition(band: PriceBand): PriceBandDefinition | undefined {
  return bandById.get(band);
}

/** 価格が帯に含まれるか。未定義の帯は絞り込みなし（true）として扱う。 */
export function matchesPriceBand(price: number, band: PriceBand): boolean {
  const definition = bandById.get(band);
  if (!definition) return true;
  if (price < definition.min) return false;
  if (definition.max !== null && price > definition.max) return false;
  return true;
}

/** 表示用ラベル（判定と同じ定義から生成するので、ラベルと挙動がズレない） */
export function getPriceBandLabel(band: PriceBand): string {
  return bandById.get(band)?.shortLabel ?? band;
}

export function getPriceBandFullLabel(band: PriceBand): string {
  return bandById.get(band)?.label ?? band;
}

/** 絞り込みUI用の選択肢 */
export const PRICE_BAND_OPTIONS: { id: PriceBand; label: string }[] = PRICE_BANDS.map((band) => ({
  id: band.id,
  label: band.label,
}));